/**
 * Owner: Masa Bayu
 * File: confidence.ts
 * Date:Time: 2026-09-18 15:50
 * Description: Confidence records for IR transforms — low scores require review.
 */

import {
  QX_DOCUMENT_CONFIDENCE_TARGETS,
  QX_DOCUMENT_REVIEW_CONFIDENCE_THRESHOLD,
  type QXDocumentConfidenceTarget,
} from './codes';
import { assertConfidence, isRecord } from './guards';

export interface QXDocumentConfidence {
  readonly target: QXDocumentConfidenceTarget;
  readonly type: string;
  readonly confidence: number;
  readonly evidence: readonly string[];
}

export interface CreateQXDocumentConfidenceOptions {
  readonly target: QXDocumentConfidenceTarget;
  readonly type: string;
  readonly confidence: number;
  readonly evidence?: readonly string[];
}

export function createQXDocumentConfidence(
  options: CreateQXDocumentConfidenceOptions,
): QXDocumentConfidence {
  if (!isRecord(options)) {
    throw new Error('Document confidence must be an object');
  }
  if (!(QX_DOCUMENT_CONFIDENCE_TARGETS as readonly string[]).includes(options.target)) {
    throw new Error('Document confidence target is unknown');
  }
  if (typeof options.type !== 'string' || options.type.trim().length === 0) {
    throw new Error('Document confidence type must be a non-empty string');
  }
  assertConfidence(options.confidence, 'confidence');
  const evidence = (options.evidence ?? []).map((entry) => {
    if (typeof entry !== 'string' || entry.trim().length === 0) {
      throw new Error('Document confidence evidence must be non-empty strings');
    }
    return entry.trim();
  });
  return Object.freeze({
    target: options.target,
    type: options.type.trim(),
    confidence: options.confidence,
    evidence: Object.freeze(evidence),
  });
}

export function requiresHumanReview(
  confidence: number,
  threshold: number = QX_DOCUMENT_REVIEW_CONFIDENCE_THRESHOLD,
): boolean {
  assertConfidence(confidence, 'confidence');
  assertConfidence(threshold, 'threshold');
  return confidence < threshold;
}
